import { all, call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import SuperFetch from 'library/helpers/superFetch';
import { Notification } from 'zComponents/atoms';
import { siteTrialStaffActions as A, STALE_DECISION_CODES } from './slice';

/**
 * Site-trial staff console sagas (PHASE2-PLAN P2-8a / B16). Every call goes to the staff member's own
 * org (SuperFetch's default orgID): the URL org scopes the STAFF role, never the data.
 * Contract: App Server docs/features/billing/site-trial-staff-console.md.
 */
export const BASE = '/billing-admin/site-trial';

const isOk = (res) => res && (res.statusCode === 200 || res.statusCode === 201);
const refusal = (res) => ({
	errorCode: res?.errorCode || null,
	error: res?.error || res?.message || res?.errors || 'Request failed.',
	currentVersion: res?.data?.currentVersion || null
});
const compact = (q) =>
	Object.fromEntries(Object.entries(q || {}).filter(([, v]) => v !== null && v !== undefined && v !== ''));

function* read(slug, query, success, failure) {
	const res = yield call(SuperFetch.get, slug, {}, compact(query));
	if (isOk(res)) {
		yield put(success(res.data));
		return res.data;
	}
	yield put(failure(refusal(res).error));
	return null;
}

export function* loadControlState() {
	yield* read(`${BASE}/control-state`, {}, A.loadControlStateSuccess, A.loadControlStateError);
}

export function* loadCatalog() {
	yield* read(`${BASE}/actions`, {}, A.loadCatalogSuccess, A.loadCatalogError);
}

export function* loadAgreements() {
	const filter = yield select((s) => s.SiteTrialStaff?.agreementFilter || {});
	yield* read(`${BASE}/agreements`, { state: filter.state }, A.loadAgreementsSuccess, A.loadAgreementsError);
}

export function* loadAgreement({ payload }) {
	yield* read(`${BASE}/agreements/${payload}`, {}, A.loadAgreementSuccess, A.loadAgreementError);
}

export function* loadHolds() {
	yield* read(`${BASE}/holds`, {}, A.loadHoldsSuccess, A.loadHoldsError);
}

export function* loadHoldDetail({ payload }) {
	yield* read(`${BASE}/holds/${payload}`, {}, A.loadHoldDetailSuccess, A.loadHoldDetailError);
}

export function* loadSessions() {
	yield* read(
		`${BASE}/install-sessions`,
		{ partnerless: true, status: 'open' },
		A.loadSessionsSuccess,
		A.loadSessionsError
	);
}

/** Refresh everything an action can change: the open agreement, the holds list, the control view. */
function* refreshAfterAction() {
	const agreementID = yield select((s) => s.SiteTrialStaff?.selectedAgreementID);
	if (agreementID) yield put(A.loadAgreement(agreementID));
	yield put(A.loadHolds());
	yield put(A.loadControlState());
}

/**
 * Send the pending decision. Body: { operationID, version, note, reviewedThroughChangeID? }. The
 * operationID comes from `pendingAction` and is the SAME on a retry, so the server answers a retry
 * with the recorded result instead of a second change.
 */
export function* runAction({ payload }) {
	const pending = yield select((s) => s.SiteTrialStaff?.pendingAction);
	if (!pending) return;
	const body = {
		operationID: pending.operationID,
		version: pending.version,
		note: payload?.note,
		// Sent whenever the caller set it, null included: a D35 release with no repaired row in the billed
		// period reviews "through nothing" deliberately, and the server records exactly that.
		...(payload && 'reviewedThroughChangeID' in payload
			? { reviewedThroughChangeID: payload.reviewedThroughChangeID ?? null }
			: {})
	};
	const res = yield call(SuperFetch.post, `${BASE}/obligations/${pending.obligationID}/${pending.action}`, body);
	if (isOk(res)) {
		yield put(A.runActionSuccess(res.data));
		Notification('success', res.data?.replayed ? 'Already applied. The recorded result is shown.' : 'Applied.');
		yield* refreshAfterAction();
		return;
	}
	const err = refusal(res);
	yield put(A.runActionError(err));
	Notification('error', err.error);
	// The row or its history moved under the screen: the slice has closed the dialog, so reload what the
	// staff member reopens it on, including the hold's history for a D35 release.
	if (STALE_DECISION_CODES.includes(err.errorCode)) {
		yield* refreshAfterAction();
		if (pending.obligationID != null) yield put(A.loadHoldDetail(pending.obligationID));
	}
}

export function* loadConsole() {
	yield all([
		put(A.loadControlState()),
		put(A.loadCatalog()),
		put(A.loadAgreements()),
		put(A.loadHolds()),
		put(A.loadSessions())
	]);
}

export const CONSOLE_OPENED = 'siteTrialStaff/consoleOpened';
export const openConsole = () => ({ type: CONSOLE_OPENED });

export default function* siteTrialStaffSagas() {
	yield all([
		takeLatest(CONSOLE_OPENED, loadConsole),
		takeLatest(A.loadControlState.type, loadControlState),
		takeLatest(A.loadCatalog.type, loadCatalog),
		takeLatest(A.loadAgreements.type, loadAgreements),
		takeLatest(A.loadAgreement.type, loadAgreement),
		takeLatest(A.loadHolds.type, loadHolds),
		takeLatest(A.loadHoldDetail.type, loadHoldDetail),
		takeLatest(A.loadSessions.type, loadSessions),
		// takeLeading: a second click while one decision is in flight is dropped, not queued.
		takeLeading(A.runAction.type, runAction)
	]);
}
